// src/components/DepartmentListView.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { ROUTES } from '../constants/routes';

const DepartmentListView = () => {
    const { apiService } = useOutletContext();
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const fetchDepartments = useCallback(async () => {
        if (!apiService) {
             setError("API service is not available.");
             setLoading(false);
             return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await apiService.getDepartmentList();
            setDepartments(result.map(record => ({ name: record.department })));
        } catch (err) {
            console.error("Error fetching department list:", err);
            setError("Failed to load department data. " + err.message);
            setDepartments([]);
        } finally {
            setLoading(false);
        }
    }, [apiService]);

    useEffect(() => {
        fetchDepartments();
    }, [fetchDepartments]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div 
                    className="animate-spin rounded-full h-12 w-12 border-b-2"
                    style={{ borderColor: 'var(--pallium-primary-purple)' }}
                ></div>
                <span className="ml-3" style={{ color: 'var(--pallium-text-secondary)' }}>
                    Loading departments...
                </span>
            </div>
        );
    }

    if (error) {
        return (
            <div 
                className="p-4 rounded-md"
                style={{ backgroundColor: 'var(--pallium-alert-error-bg)', color: 'var(--pallium-error-red)' }}
            >
                <strong>Error:</strong> {error} 
                <button 
                    onClick={fetchDepartments} 
                    className="ml-2 text-white font-bold py-1 px-2 rounded text-sm transition-opacity"
                    style={{ backgroundColor: 'var(--pallium-error-red)' }}
                    onMouseOver={(e) => e.target.style.opacity = '0.8'}
                    onMouseOut={(e) => e.target.style.opacity = '1'}
                >
                    Retry
                </button>
            </div>
        );
    }

    if (departments.length === 0) {
        return (
            <div className="p-4 text-center" style={{ color: 'var(--pallium-text-secondary)' }}>
                No departments found.
            </div>
        );
    }

    return (
        <div className="container mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="dept-list">
                {departments.map((dept) => (
                    <div 
                        key={dept.name} 
                        className="group rounded-xl p-6 cursor-pointer transition-all duration-200"
                        style={{ 
                            backgroundColor: 'var(--pallium-white)',
                            boxShadow: 'var(--pallium-shadow-md)',
                            border: '1px solid var(--pallium-stroke-light)'
                        }}
                        onClick={() => navigate(`${ROUTES.OPERATIONS}/${dept.name}/overview`)}
                        onMouseOver={(e) => {
                            e.currentTarget.style.boxShadow = 'var(--pallium-shadow-lg)';
                            e.currentTarget.style.borderColor = 'var(--pallium-purple-2)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.boxShadow = 'var(--pallium-shadow-md)';
                            e.currentTarget.style.borderColor = 'var(--pallium-stroke-light)';
                        }}
                        data-testid="dept-card"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                {/* Icon circle */}
                                <div 
                                    className="w-12 h-12 min-w-[3rem] rounded-full flex items-center justify-center flex-shrink-0"
                                    style={{ 
                                        background: 'var(--pallium-gradient-purple-subtle)'
                                    }}
                                >
                                    <svg 
                                        className="w-6 h-6 flex-shrink-0"
                                        style={{ color: 'var(--pallium-primary-purple)' }}
                                        xmlns="http://www.w3.org/2000/svg" 
                                        viewBox="0 0 20 20" 
                                        fill="currentColor"
                                    >
                                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 
                                        className="text-lg font-semibold"
                                        style={{ color: 'var(--pallium-primary-purple)' }}
                                    >
                                        <Link 
                                            to={`${ROUTES.OPERATIONS}/${dept.name}/overview`}
                                            onClick={(e) => e.stopPropagation()}
                                            className="hover:underline"
                                        >
                                            {dept.name}
                                        </Link>
                                    </h3>
                                    <p 
                                        className="text-sm mt-1"
                                        style={{ color: 'var(--pallium-text-secondary)' }}
                                    >
                                        View department overview
                                    </p>
                                </div>
                            </div>
                            {/* Arrow indicator */}
                            <svg 
                                className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200"
                                style={{ color: 'var(--pallium-icon-gray)' }}
                                xmlns="http://www.w3.org/2000/svg" 
                                viewBox="0 0 20 20" 
                                fill="currentColor"
                            >
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DepartmentListView;
